import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShoppingBag, TrendingUp, CheckCircle, AlertCircle,
  Clock, Search, X, ChevronDown,
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LIST = ['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered', 'cancelled'];

const STATUS_META = {
  pending:          { label: 'Pending',          color: 'bg-yellow-100 text-yellow-700' },
  confirmed:        { label: 'Confirmed',         color: 'bg-blue-100 text-blue-700' },
  preparing:        { label: 'Preparing',         color: 'bg-indigo-100 text-indigo-700' },
  ready:            { label: 'Ready',             color: 'bg-teal-100 text-teal-700' },
  out_for_delivery: { label: 'Out for Delivery',  color: 'bg-orange-100 text-orange-700' },
  delivered:        { label: 'Delivered',         color: 'bg-green-100 text-green-700' },
  cancelled:        { label: 'Cancelled',         color: 'bg-red-100 text-red-700' },
};

const PAYMENT_META = {
  paid:    'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  failed:  'bg-red-100 text-red-700',
};

export default function AdminOrders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStatus, setActiveStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [vendors, setVendors] = useState([]);
  const [vendorFilter, setVendorFilter] = useState('');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 200 });
      if (activeStatus !== 'all') params.set('status', activeStatus);
      if (vendorFilter) params.set('vendor_id', vendorFilter);
      if (search) params.set('search', search);

      const res = await axios.get(`${API_URL}/api/admin/orders?${params}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [activeStatus, vendorFilter, search]);

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/vendors`, { withCredentials: true });
      setVendors(res.data || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchVendors(); }, []);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const clearSearch = () => { setSearch(''); setSearchInput(''); };

  const sc = data?.status_counts || {};
  const totalActive = ['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery']
    .reduce((s, k) => s + (sc[k] || 0), 0);

  return (
    <div data-testid="admin-orders-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-text-primary">Orders</h1>
        <p className="font-body text-text-muted text-sm mt-1">All orders placed across the platform</p>
      </div>

      {/* ── Quick stats ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Orders',   value: data?.total_orders ?? '—',  icon: ShoppingBag,  color: 'text-brand-primary', bg: 'bg-brand-primary/10' },
          { label: 'Active',         value: data ? totalActive : '—',   icon: Clock,        color: 'text-indigo-600',    bg: 'bg-indigo-100' },
          { label: 'Delivered',      value: sc.delivered ?? '—',        icon: CheckCircle,  color: 'text-green-600',     bg: 'bg-green-100' },
          { label: 'Revenue',        value: data ? `₹${(data.total_revenue || 0).toLocaleString('en-IN')}` : '—', icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-100' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-white rounded-xl border border-surface-border p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
              <Icon size={18} className={color} />
            </div>
            <div>
              <p className="text-xs text-text-muted">{label}</p>
              <p className="font-heading font-bold text-lg text-text-primary leading-tight">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filters ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-surface-border p-4 mb-4 space-y-3">
        {/* Status tabs */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveStatus('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              activeStatus === 'all'
                ? 'bg-brand-primary text-white'
                : 'bg-background-secondary text-text-secondary hover:bg-surface-border'
            }`}
          >
            All {data?.total_orders ? `(${data.total_orders})` : ''}
          </button>
          {STATUS_LIST.map((s) => (
            <button
              key={s}
              onClick={() => setActiveStatus(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                activeStatus === s
                  ? 'bg-brand-primary text-white'
                  : 'bg-background-secondary text-text-secondary hover:bg-surface-border'
              }`}
            >
              {STATUS_META[s].label} {sc[s] ? `(${sc[s]})` : ''}
            </button>
          ))}
        </div>

        {/* Search + vendor filter row */}
        <div className="flex flex-col sm:flex-row gap-2">
          <form onSubmit={handleSearch} className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search order #, customer or vendor…"
              className="pl-9 pr-8 rounded-xl text-sm"
            />
            {searchInput && (
              <button type="button" onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary">
                <X size={14} />
              </button>
            )}
          </form>

          {/* Vendor dropdown */}
          <div className="relative sm:w-56">
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
            <select
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
              className="w-full appearance-none rounded-xl border border-surface-border bg-white px-3 py-2 pr-8 text-sm font-body text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            >
              <option value="">All Vendors</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-surface-border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
          </div>
        ) : !data?.orders?.length ? (
          <div className="py-16 text-center">
            <AlertCircle size={32} className="text-text-muted mx-auto mb-3" />
            <p className="text-text-muted font-body">No orders found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-border bg-background-secondary">
                  {['Order #', 'Customer', 'Vendor', 'Amount', 'Items', 'Status', 'Payment', 'Date'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-text-muted whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.orders.map((o) => {
                  const sm = STATUS_META[o.status] || { label: o.status, color: 'bg-gray-100 text-gray-600' };
                  const pm = PAYMENT_META[o.payment_status] || 'bg-gray-100 text-gray-600';
                  return (
                    <tr key={o.id} className="border-b border-surface-border last:border-0 hover:bg-background-secondary/50 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-brand-primary whitespace-nowrap">{o.order_number}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-text-primary">{o.customer_name || '—'}</p>
                        {o.customer_apartment_name && (
                          <p className="text-xs text-text-muted">{o.customer_apartment_name}{o.customer_room_number ? ` · ${o.customer_room_number}` : ''}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-text-secondary whitespace-nowrap">{o.vendor_name || '—'}</td>
                      <td className="px-4 py-3 font-semibold whitespace-nowrap">₹{(o.total_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-text-secondary text-center">{o.item_count}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${sm.color}`}>{sm.label}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${pm}`}>
                          {o.payment_status?.charAt(0).toUpperCase() + o.payment_status?.slice(1)}
                        </span>
                        <span className="ml-1 text-xs text-text-muted uppercase">{o.payment_method}</span>
                      </td>
                      <td className="px-4 py-3 text-text-muted text-xs whitespace-nowrap">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-surface-border bg-background-secondary">
              <p className="text-xs text-text-muted">Showing {data.orders.length} order{data.orders.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
